import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '../../components/Layout/AdminLayout';
import { api } from '../../services/api';
import type { ApplicationSummary, ApplicationStatus } from '@vedjegy/shared';
import { APPLICATION_STATUS_LABELS, APPLICATION_STATUS_CONFIG } from '../../utils/applicationStatus';

const STATUS_LABELS  = APPLICATION_STATUS_LABELS;
const STATUS_COLORS  = APPLICATION_STATUS_CONFIG;

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('hu-HU', { year: 'numeric', month: '2-digit', day: '2-digit' });
}

export default function AdminApplicationsPage() {
  const navigate = useNavigate();
  const [apps, setApps]             = useState<ApplicationSummary[]>([]);
  const [loading, setLoading]       = useState(true);
  const [error, setError]           = useState('');
  const [total, setTotal]           = useState(0);
  const [page, setPage]             = useState(1);

  // Filters
  const [statusFilter, setStatusFilter]       = useState('');
  const [typeFilter, setTypeFilter]           = useState('');
  const [excellentFilter, setExcellentFilter] = useState('');
  const [searchRaw, setSearchRaw]             = useState('');
  const [search, setSearch]                   = useState('');
  const searchTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const load = useCallback(async (pg: number, st: string, ty: string, ex: string, sr: string) => {
    setLoading(true);
    setError('');
    try {
      const res = await api.admin.listApplications({
        status:    st || undefined,
        type:      ty || undefined,
        excellent: ex || undefined,
        search:    sr || undefined,
        page:      pg,
        limit:     50,
      });
      setApps(res.applications);
      setTotal(res.pagination.total);
    } catch {
      setError('Nem sikerült betölteni a pályázatokat.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void load(page, statusFilter, typeFilter, excellentFilter, search); }, [load, page, statusFilter, typeFilter, excellentFilter, search]);

  function handleSearchChange(v: string) {
    setSearchRaw(v);
    if (searchTimer.current) clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => {
      setSearch(v);
      setPage(1);
    }, 300);
  }

  function handleStatusFilter(v: string)    { setStatusFilter(v);    setPage(1); }
  function handleTypeFilter(v: string)      { setTypeFilter(v);      setPage(1); }
  function handleExcellentFilter(v: string) { setExcellentFilter(v); setPage(1); }

  const totalPages = Math.ceil(total / 50);

  return (
    <AdminLayout>
      <div style={{ padding: '32px 40px' }}>
        {/* Header */}
        <div style={{ marginBottom: 24, display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16 }}>
          <div>
            <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '.12em', textTransform: 'uppercase', color: 'var(--secondary-foreground)', marginBottom: 4 }}>
              ADMIN
            </div>
            <h1 style={{ margin: 0, fontSize: 24, fontWeight: 700, color: 'var(--primary)' }}>Pályázók</h1>
          </div>
          <div style={{ fontSize: 14, color: 'var(--muted-foreground)', paddingTop: 28 }}>{total} pályázat</div>
        </div>

        {/* Filter bar */}
        <div style={{ display: 'flex', gap: 10, marginBottom: 20, flexWrap: 'wrap', alignItems: 'center' }}>
          <input
            type="text"
            value={searchRaw}
            onChange={(e) => handleSearchChange(e.target.value)}
            placeholder="Keresés névre, adószámra…"
            style={{ width: 240, height: 36, padding: '0 12px', border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)', fontSize: 13, fontFamily: 'var(--font-sans)', color: 'var(--primary)', background: '#fff' }}
          />
          <select
            value={statusFilter}
            onChange={(e) => handleStatusFilter(e.target.value)}
            style={{ height: 36, padding: '0 10px', border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)', fontSize: 13, fontFamily: 'var(--font-sans)', color: 'var(--primary)', background: '#fff' }}
          >
            <option value="">Minden státusz</option>
            {(Object.keys(STATUS_LABELS) as ApplicationStatus[]).map((s) => (
              <option key={s} value={s}>{STATUS_LABELS[s]}</option>
            ))}
          </select>
          <select
            value={typeFilter}
            onChange={(e) => handleTypeFilter(e.target.value)}
            style={{ height: 36, padding: '0 10px', border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)', fontSize: 13, fontFamily: 'var(--font-sans)', color: 'var(--primary)', background: '#fff' }}
          >
            <option value="">Minden típus</option>
            <option value="new">Új pályázó</option>
            <option value="renewal">Megújító</option>
          </select>
          <select
            value={excellentFilter}
            onChange={(e) => handleExcellentFilter(e.target.value)}
            style={{ height: 36, padding: '0 10px', border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)', fontSize: 13, fontFamily: 'var(--font-sans)', color: 'var(--primary)', background: '#fff' }}
          >
            <option value="">Minden pályázat</option>
            <option value="suggested">⭐ Kiváló jelöltek</option>
            <option value="approved">✨ Elfogadott kiváló</option>
            <option value="rejected">Elutasított jelölés</option>
          </select>
          {(statusFilter || typeFilter || excellentFilter || search) && (
            <button
              className="btn btn-ghost btn-sm"
              onClick={() => { setStatusFilter(''); setTypeFilter(''); setExcellentFilter(''); setSearchRaw(''); setSearch(''); setPage(1); }}
            >
              Szűrők törlése ×
            </button>
          )}
        </div>

        {/* Error */}
        {error && (
          <div className="alert alert-destructive" style={{ marginBottom: 16 }}>
            <span>⚠</span><div><p>{error}</p></div>
          </div>
        )}

        {/* Table */}
        <div style={{ background: '#fff', border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', overflow: 'hidden', boxShadow: 'var(--shadow-sm)' }}>
          {/* Table header */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 120px 100px 160px 140px 180px', gap: 0, borderBottom: '1px solid var(--border)', background: 'var(--muted)', padding: '10px 20px' }}>
            {['Vállalkozás', 'Típus', 'Év', 'Státusz', 'Beküldve', 'Bíráló'].map((h) => (
              <div key={h} style={{ fontSize: 11, fontWeight: 700, letterSpacing: '.08em', textTransform: 'uppercase', color: 'var(--secondary-foreground)' }}>{h}</div>
            ))}
          </div>

          {/* Rows */}
          {loading ? (
            <div style={{ padding: '40px 20px', color: 'var(--muted-foreground)', fontSize: 14, textAlign: 'center' }}>Betöltés…</div>
          ) : apps.length === 0 ? (
            <div style={{ padding: '40px 20px', color: 'var(--muted-foreground)', fontSize: 14, textAlign: 'center' }}>
              {statusFilter || typeFilter || search ? 'Nincs a szűrőknek megfelelő pályázat.' : 'Még nincs pályázat.'}
            </div>
          ) : (
            apps.map((app, idx) => {
              const sc = STATUS_COLORS[app.status];
              return (
                <div
                  key={app.id}
                  onClick={() => navigate('/admin/applications/' + app.id)}
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 120px 100px 160px 140px 180px',
                    gap: 0,
                    padding: '12px 20px',
                    borderBottom: idx < apps.length - 1 ? '1px solid var(--border)' : 'none',
                    cursor: 'pointer',
                    alignItems: 'center',
                    transition: 'background .1s',
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--muted)')}
                  onMouseLeave={(e) => (e.currentTarget.style.background = '')}
                >
                  {/* Company */}
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span style={{ fontSize: 14, fontWeight: 700, color: 'var(--primary)' }}>{app.companyName}</span>
                      {app.excellentSuggested && app.excellentApproved === null && (
                        <span title="Kiváló pályázó jelölt — döntés folyamatban" style={{ fontSize: 11, fontWeight: 700, padding: '2px 7px', borderRadius: 12, background: '#fef3c7', color: '#92400e', border: '1px solid #fde68a' }}>⭐ Jelölt</span>
                      )}
                      {app.excellentApproved === true && (
                        <span title="Kiváló pályázó — elfogadva" style={{ fontSize: 11, fontWeight: 700, padding: '2px 7px', borderRadius: 12, background: '#d1fae5', color: '#065f46', border: '1px solid #6ee7b7' }}>✨ Kiváló</span>
                      )}
                      {app.excellentApproved === false && (
                        <span title="Kiváló jelölés elutasítva" style={{ fontSize: 11, fontWeight: 700, padding: '2px 7px', borderRadius: 12, background: '#f3f4f6', color: '#6b7280', border: '1px solid #d1d5db' }}>⭐ Elutasítva</span>
                      )}
                    </div>
                    <div style={{ fontSize: 12, color: 'var(--muted-foreground)', marginTop: 2 }}>{app.taxId}</div>
                  </div>
                  {/* Type */}
                  <div style={{ fontSize: 13, color: 'var(--primary)' }}>
                    {app.type === 'new' ? 'Új' : 'Megújítás'}
                  </div>
                  {/* Year */}
                  <div style={{ fontSize: 13, color: 'var(--primary)' }}>{app.year}</div>
                  {/* Status */}
                  <div>
                    <span style={{ display: 'inline-block', padding: '3px 9px', borderRadius: 20, fontSize: 12, fontWeight: 700, background: sc.bg, color: sc.text }}>
                      {STATUS_LABELS[app.status]}
                    </span>
                  </div>
                  {/* Submitted at */}
                  <div style={{ fontSize: 13, color: 'var(--primary)' }}>{formatDate(app.submittedAt)}</div>
                  {/* Reviewers */}
                  <div style={{ fontSize: 12, color: 'var(--muted-foreground)' }}>
                    {app.reviewers.length === 0
                      ? <span style={{ color: 'var(--muted-foreground)', fontStyle: 'italic' }}>—</span>
                      : app.reviewers.map((r) => r.email.split('@')[0]).join(', ')
                    }
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div style={{ display: 'flex', gap: 8, justifyContent: 'center', marginTop: 20, alignItems: 'center' }}>
            <button className="btn btn-ghost btn-sm" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1}>← Előző</button>
            <span style={{ fontSize: 13, color: 'var(--muted-foreground)' }}>{page} / {totalPages}</span>
            <button className="btn btn-ghost btn-sm" onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page === totalPages}>Következő →</button>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
